"use client";

import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, m, domAnimation, LazyMotion } from "framer-motion";
import { Card, CardContent } from "@components/components/ui/card";
import { Button } from "@components/components/ui/button";
import Link from "next/link";

/**
 * Cookie consent preferences
 */
type ConsentValue = "accepted" | "rejected";

/**
 * Cookie consent storage key
 */
const CONSENT_STORAGE_KEY = "cookie_consent_preference";

/**
 * Professional cookie consent banner component
 *
 * Displays a GDPR-compliant cookie consent banner with smooth animations
 * and professional styling. Manages user consent preferences in localStorage.
 */
export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  /**
   * Initialize component and check for existing consent
   */
  useEffect(() => {
    const existingConsent = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!existingConsent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  /**
   * Handle user consent selection
   */
  const handleConsentSelection = useCallback((consent: ConsentValue): void => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, consent);
      setIsVisible(false);

      // Optional: Trigger analytics or other consent-dependent services
      if (consent === "accepted") {
        // Initialize analytics, tracking, etc.
        console.log("Cookie consent accepted - services can be initialized");
      }
    } catch (error) {
      console.error("Failed to save cookie consent preference:", error);
    }
  }, []);

  /**
   * Animation configuration
   */
  const animationConfig = {
    initial: { opacity: 0, y: 60, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 60, scale: 0.95 },
    transition: {
      duration: 0.5,
      ease: [0.4, 0.0, 0.2, 1], // Custom easing for professional feel
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence mode="wait">
        {isVisible && (
          <m.div
            {...animationConfig}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4"
            role="dialog"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-description"
          >
            <div className="max-w-[30rem] mr-auto">
              <Card className="border border-gray-200 bg-white shadow-lg">
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4">
                    <div className="space-y-2">
                      <h3
                        id="cookie-consent-title"
                        className="text-base font-semibold text-gray-900"
                      >
                        Privacy & Cookie Notice
                      </h3>
                      <p
                        id="cookie-consent-description"
                        className="text-sm text-gray-600 leading-relaxed"
                      >
                        We use essential and analytics cookies to enhance your
                        browsing experience and improve our services. By
                        accepting, you consent to our use of cookies as outlined
                        in our{" "}
                        <Link
                          href="/privacy-policy"
                          className="font-medium text-primaryDefault underline-offset-4 hover:underline focus:underline focus:outline-none"
                          tabIndex={0}
                        >
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConsentSelection("rejected")}
                        className="w-auto border-gray-300 text-gray-700 hover:bg-gray-50"
                        aria-label="Reject cookies"
                      >
                        Reject All
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleConsentSelection("accepted")}
                        className="w-auto bg-themeColor text-white"
                        aria-label="Accept cookies"
                      >
                        Accept All
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}
